import { createContext, useContext } from 'react';

import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

/**
 * Larger text, for the Care hub concept: an 18px name over a 16px description,
 * without changing the rows anywhere else. A trial, so the name's size is sx
 * until it earns a variant.
 */
export const ClaimRowLarge = createContext(false);

// A claim, as a row. The CAM control panel's forms row read back to the family:
// a round icon, the name, a line of context, and how much of it is answered.
//
// Shared between the screen that says what the intake filled in and the Claims
// tab of the profile, because they are the same object in two places and were
// drawn twice before this.
const ClaimRow = ({
  icon,
  name,
  description,
  count,
  lead = false,
  cherry = false,
  filled,
  onClick,
}: {
  /** Left out where nobody would choose one, like a care expert's list. */
  icon?: string;
  name: string;
  description: string;
  /**
   * How much of it is answered, e.g. "3 of 14 answered".
   *
   * A chip, and the row draws it rather than taking one: it used to be a
   * caption line on the claims tab and a Chip on the screen that chooses
   * between them, so the same claim wore two different counts depending on
   * which screen you reached it from. A string in, a chip out, and there is
   * nowhere left for that to drift.
   */
  count?: string;
  /** Coral rather than lime: the one to do next. */
  lead?: boolean;
  /**
   * Light cherry rather than lime: a row that is there to be found, not one
   * that applies. Leaves lime to mean "this could be theirs". The palette has
   * one cherry, so the light is that cherry at low opacity.
   */
  cherry?: boolean;
  /**
   * A lead row on a solid pale fill, the one thing to do on Home: peach (Warm
   * Orange) with the coral edge, teal (Marine Green) with a mid green edge, or
   * cherry with a cherry edge.
   * Solid, because a see-through tint went murky on Gardenia. The icon's
   * circle goes white so it still shows.
   */
  filled?: 'peach' | 'teal' | 'cherry';
  onClick?: () => void;
}) => {
  const large = useContext(ClaimRowLarge);
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        borderColor: !lead
          ? undefined
          : filled === 'teal'
            ? 'custom.marineGreen.300'
            : filled === 'cherry'
              ? 'custom.cherry.500'
              : 'custom.coral.500',
        // Coral's own pale reads pink, so peach is Warm Orange, very pale.
        bgcolor: !lead
          ? undefined
          : filled === 'teal'
            ? (t) => lighten(t.palette.custom.marineGreen[500], 0.9)
            : filled === 'cherry'
              ? (t) => lighten(t.palette.custom.cherry[500], 0.9)
              : filled === 'peach'
                ? (t) => lighten(t.palette.custom.warmOrange[500], 0.88)
                : undefined,
      }}
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: lead
                ? filled
                  ? 'background.paper'
                  : 'custom.coral.50'
                : cherry
                  ? (t) => alpha(t.palette.custom.cherry[500], 0.12)
                  : 'custom.limeGreen.200',
              color: lead
                ? filled === 'teal'
                  ? 'custom.marineGreen.500'
                  : filled === 'cherry'
                    ? 'custom.cherry.500'
                    : 'custom.coral.500'
                : cherry
                  ? 'custom.cherry.500'
                  : 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon={icon as never} fontSize={18} />
          </Box>
        )}
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
          <Typography
            variant='h6'
            sx={large ? { fontSize: '1.125rem', lineHeight: 1.4 } : undefined}
          >
            {name}
          </Typography>
          <Typography
            sx={{ color: 'text.secondary' }}
            variant={large ? 'body1' : 'body2'}
          >
            {description}
          </Typography>
          {/* alignSelf so the chip is as wide as its label rather than as wide as
            the row. */}
          {count && (
            <Box sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
              <Chip size='small' label={count} />
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default ClaimRow;
